import { ErrorCodes, ErrorMessage, NoddiButton, NoddiInfoBanner } from 'noddi-ui-web';
import { getFirstErrorCode, mapErrorCodeToUiMessage } from 'noddi-util';

import { LicensePlateInput } from './LicensePlateInput';
import { FoundCar, RegNumberSearchState } from './useRegNumberSearch';

// Plate search 404s when the national registry has no such number. That is a
// normal miss, not a failed request.
const PLATE_NOT_IN_REGISTRY_CODES = new Set([
  'car_not_found',
  'car_not_found_car_info',
  'car_not_found_for_license_plate',
  'car_not_found_in_registry',
  'car_not_found_statens_vegvesen',
  'statens_vegvesen_vehicle_data_not_available',
  'statens_vegvesen_vehicle_not_found'
]);

type Props = {
  search: RegNumberSearchState;
  onCarFound: (car: FoundCar) => void;
  translations: {
    add: string;
    placeholder: string;
    invalidLicensePlate: string;
    alreadyExists: string;
  };
  errorCodes?: ErrorCodes;
};

/** Plate input + Add button over a `useRegNumberSearch` the caller owns, so a page action can submit the typed plate too. */
export const RegNumberSearchField = ({ search, onCarFound, translations, errorCodes }: Props) => {
  const {
    countryCode,
    regNumber,
    onRegNumberChange,
    inputRef,
    canSubmit,
    isSearching,
    isInvalid,
    alreadyExists,
    error
  } = search;

  const handleSearch = async () => {
    const car = await search.search();
    if (car) {
      onCarFound(car);
    }
  };

  const lookupErrorMessage = error ? mapErrorCodeToUiMessage({ error, errorCodes }) : undefined;
  const plateNotInRegistry = error != null && PLATE_NOT_IN_REGISTRY_CODES.has(getFirstErrorCode(error) ?? '');

  return (
    <div>
      <div className='flex items-center gap-3'>
        <LicensePlateInput
          ref={inputRef}
          placeholder={translations.placeholder}
          countryCode={countryCode}
          value={regNumber}
          onChange={onRegNumberChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (canSubmit) {
                // oxlint-disable-next-line typescript/no-floating-promises -- handleSearch only refetches the plate lookup; failures render from the query's error
                handleSearch();
              }
            }
          }}
        />

        <NoddiButton
          variant='outline'
          startIcon='Plus'
          // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleSearch(); React discards the returned promise, so a rejection is unhandled unless the body catches
          onClick={handleSearch}
          loading={isSearching}
          disabled={!canSubmit}
        >
          {translations.add}
        </NoddiButton>
      </div>

      {lookupErrorMessage && plateNotInRegistry && (
        <div className='mb-5'>
          <NoddiInfoBanner variant='warning' texts={[{ value: lookupErrorMessage }]} />
        </div>
      )}

      {lookupErrorMessage && !plateNotInRegistry && (
        <div className='mb-5'>
          <ErrorMessage message={lookupErrorMessage} />
        </div>
      )}

      {alreadyExists && (
        <div className='mt-2 mb-5'>
          {/* eslint-disable-next-line noddi/no-emoji-in-ui -- pointing-down emoji directs the user to the existing car rendered below */}
          <NoddiInfoBanner texts={[{ value: `${translations.alreadyExists} 👇` }]} />
        </div>
      )}

      {isInvalid && !!regNumber.length && (
        <div className='mb-5'>
          <ErrorMessage message={translations.invalidLicensePlate} />
        </div>
      )}
    </div>
  );
};
